import { Controller } from "@hotwired/stimulus"

// Handles underlying fund transaction forms with proper amount formatting
// For capital calls: provides slider, percentage, and quick-select buttons
// Percentage is of TOTAL commitment, but slider/buttons cap at maxPercentage (remaining callable)
// This matches the capital call form pattern
export default class extends Controller {
  static targets = [
    "slider", "percentageInput", "displayAmount", "hiddenAmount",
    "quickButton", "navDisplay", "multipleDisplay", "projectedNav"
  ]
  static values = {
    commitment: Number,      // Total commitment in dollars
    maxPercentage: Number,   // Remaining callable as % of total commitment (e.g., 75 means 75% remaining)
    transactionType: String, // "capital_call", "distribution", or "nav_update"
    funded: Number,          // Capital funded in dollars (for NAV gain/loss calculation)
    currentNav: Number       // Current NAV in dollars (for projected NAV calculation)
  }

  connect() {
    this.formatInitialValues()
  }

  // Format any existing values on connect
  formatInitialValues() {
    if (this.hasHiddenAmountTarget && this.hasDisplayAmountTarget) {
      const cents = parseInt(this.hiddenAmountTarget.value) || 0
      if (cents > 0) {
        this.displayAmountTarget.value = this.formatNumber(cents / 100)
        // Update slider and percentage if capital call
        if (this.hasSliderTarget && this.transactionTypeValue === "capital_call") {
          const dollars = cents / 100
          const percentage = this.commitmentValue > 0
            ? (dollars / this.commitmentValue) * 100
            : 0
          this.sliderTarget.value = Math.min(percentage, this.maxPercentageValue)
          if (this.hasPercentageInputTarget) {
            this.percentageInputTarget.value = percentage.toFixed(2)
          }
          this.updateQuickButtons(percentage)
        }
      }
    }
  }

  // Quick-select button clicked (percentage of total commitment)
  setPercentage(event) {
    event.preventDefault()
    const percentage = Math.min(parseFloat(event.currentTarget.dataset.percentage), this.maxPercentageValue)
    if (this.hasPercentageInputTarget) {
      this.percentageInputTarget.value = percentage.toFixed(2)
    }
    if (this.hasSliderTarget) {
      this.sliderTarget.value = percentage
    }
    this.updateFromPercentage(percentage)
    this.updateQuickButtons(percentage)
  }

  // Slider dragged (0 to maxPercentage, representing % of total commitment)
  sliderChanged() {
    const percentage = parseFloat(this.sliderTarget.value)
    if (this.hasPercentageInputTarget) {
      this.percentageInputTarget.value = percentage.toFixed(2)
    }
    this.updateFromPercentage(percentage)
    this.updateQuickButtons(percentage)
  }

  // Percentage text input changed (direct % of total commitment)
  percentageChanged() {
    let percentage = parseFloat(this.percentageInputTarget.value) || 0
    percentage = Math.max(0, Math.min(percentage, this.maxPercentageValue))
    if (this.hasSliderTarget) {
      this.sliderTarget.value = percentage
    }
    this.updateFromPercentage(percentage)
    this.updateQuickButtons(percentage)
  }

  // When user types in display field, update hidden cents field and sync percentage
  amountChanged() {
    const displayValue = this.displayAmountTarget.value
    const dollars = this.parseNumber(displayValue)
    const cents = Math.round(dollars * 100)
    this.hiddenAmountTarget.value = cents > 0 ? cents : ""

    // Update slider and percentage for capital calls
    if (this.hasSliderTarget && this.transactionTypeValue === "capital_call") {
      const percentage = this.commitmentValue > 0
        ? (dollars / this.commitmentValue) * 100
        : 0
      const clampedPercentage = Math.min(percentage, this.maxPercentageValue)
      this.sliderTarget.value = clampedPercentage
      if (this.hasPercentageInputTarget) {
        this.percentageInputTarget.value = clampedPercentage.toFixed(2)
      }
      this.updateQuickButtons(clampedPercentage)
    }

    // Update NAV visualization for nav_update transactions
    if (this.transactionTypeValue === "nav_update") {
      this.updateNavVisualization(dollars)
    }

    // Update projected NAV for capital calls
    if (this.transactionTypeValue === "capital_call") {
      this.updateProjectedNav(dollars)
    }
  }

  // Update the NAV visualization (arrow display) with new value
  updateNavVisualization(navDollars) {
    if (!this.hasNavDisplayTarget) return

    const fundedDollars = this.fundedValue || 0
    const multiple = fundedDollars > 0 ? navDollars / fundedDollars : 0
    const isPositive = multiple >= 1.0

    // Update NAV display (always neutral color)
    if (navDollars > 0) {
      this.navDisplayTarget.textContent = "$" + this.formatNumber(navDollars)
    } else {
      this.navDisplayTarget.textContent = "?"
    }

    // Update multiple badge display (matches multiples_helper.rb styling)
    if (this.hasMultipleDisplayTarget) {
      if (navDollars > 0 && fundedDollars > 0) {
        this.multipleDisplayTarget.textContent = multiple.toFixed(2) + "x"
      } else {
        this.multipleDisplayTarget.textContent = ""
      }

      // Badge colors matching multiples_helper.rb
      const positiveClasses = ["bg-green-100", "text-green-800", "dark:bg-green-900", "dark:text-green-300"]
      const neutralClasses = ["bg-gray-100", "text-gray-800", "dark:bg-gray-700", "dark:text-gray-300"]

      if (isPositive) {
        this.multipleDisplayTarget.classList.remove(...neutralClasses)
        this.multipleDisplayTarget.classList.add(...positiveClasses)
      } else {
        this.multipleDisplayTarget.classList.remove(...positiveClasses)
        this.multipleDisplayTarget.classList.add(...neutralClasses)
      }
    }
  }

  // Format on blur for better readability
  formatAmount() {
    const displayValue = this.displayAmountTarget.value
    const dollars = this.parseNumber(displayValue)
    if (dollars > 0) {
      this.displayAmountTarget.value = this.formatNumber(dollars)
    }
    // Also update NAV visualization on blur (even if 0, to reset display)
    if (this.transactionTypeValue === "nav_update") {
      this.updateNavVisualization(dollars)
    }
  }

  // Update amount from percentage (of total commitment)
  updateFromPercentage(percentage) {
    const dollars = this.commitmentValue * (percentage / 100)
    const cents = Math.round(dollars * 100)
    this.displayAmountTarget.value = this.formatNumber(dollars)
    this.hiddenAmountTarget.value = cents > 0 ? cents : ""

    // Update projected NAV for capital calls
    this.updateProjectedNav(dollars)
  }

  // Update the projected NAV display (current NAV + new call amount)
  updateProjectedNav(callAmountDollars) {
    if (!this.hasProjectedNavTarget) return
    if (this.transactionTypeValue !== "capital_call") return

    const newTotal = this.currentNavValue + callAmountDollars
    this.projectedNavTarget.textContent = "$" + this.formatNumber(newTotal)
  }

  // Highlight the active quick-select button
  updateQuickButtons(percentage) {
    if (!this.hasQuickButtonTarget) return

    this.quickButtonTargets.forEach((btn) => {
      const btnPercentage = parseFloat(btn.dataset.percentage)
      const isActive = Math.abs(btnPercentage - percentage) < 0.01

      if (isActive) {
        btn.classList.remove("bg-white", "dark:bg-gray-800", "text-gray-900", "dark:text-white", "hover:bg-gray-100", "dark:hover:bg-gray-700")
        btn.classList.add("bg-primary-700", "dark:bg-primary-600", "text-white")
      } else {
        btn.classList.remove("bg-primary-700", "dark:bg-primary-600", "text-white")
        btn.classList.add("bg-white", "dark:bg-gray-800", "text-gray-900", "dark:text-white", "hover:bg-gray-100", "dark:hover:bg-gray-700")
      }
    })
  }

  // Format number with commas (e.g., 1,000,000)
  formatNumber(num) {
    return Math.round(num).toLocaleString("en-US")
  }

  // Parse number from formatted string
  parseNumber(str) {
    if (!str) return 0
    return parseFloat(str.replace(/[^0-9.]/g, "")) || 0
  }
}
